import { clarity } from 'react-microsoft-clarity';

import * as Sentry from '@sentry/browser';
import moment from 'moment';

import type { Deal, Session } from '@cobliteam/common-types';

import type { UserAttributes } from '@cobliteam/api-lib';

import { Analytics } from '@cobliteam/analytics';
// root package.json
import datadogRUM from '@cobliteam/datadog-rum';
import { Notifications } from '@cobliteam/notifications';
import { Environment } from '@cobliteam/react-environments';

import packageJSON from '../../../../package.json';

const env = Environment.getInstance().getEnvironment();

export const configureOneSignal = ({ user, fleet }: Session) => {
  Notifications.initialize(fleet.id, fleet.name, user.id);
};

export const configureAnalytics = ({ user, fleet }: Session) => {
  Analytics.setUser(user.id, user.name, user.email, user.role, fleet.id, fleet.name);
};

export const configureSentry = ({ user, fleet }: Session) => {
  Sentry.configureScope((scope) => {
    scope.setUser({ id: user.id, email: user.email, username: user.name });
    scope.setTag('fleetId', fleet.id);
    scope.setTag('fleetName', fleet.name);
  });
};

export const configureDatadogRUM = (session: Session, sampleRate: number, replaySampleRate: number, userAttributes?: UserAttributes | null) => {
  const isLocalhost = window.location.href.includes('localhost');
  const isPreview = window.location.href.includes('dashboard.preview.cobli.co');

  // when running locally or in preview, we don't want to send any data to datadog
  const envSampleRate = isLocalhost || isPreview ? 0 : sampleRate;
  const envReplaySampleRate = isLocalhost || isPreview ? 0 : replaySampleRate;

  const configuration = {
    service: 'herbie-dashboard',
    env: env.name,
    version: packageJSON.version,
    sessionSampleRate: envSampleRate,
    sessionReplaySampleRate: envReplaySampleRate,
    trackResources: true,
    trackLongTasks: true,
    traceIntegration: true,
    trackFrustrations: true,
    trackUserInteractions: true,
  };

  const user = {
    id: session?.user?.id,
    name: session?.user?.name,
    email: session?.user?.email,
    role: session?.user?.role,
    app_version: configuration.version,
    fleet: {
      id: session?.fleet?.id,
      name: session?.fleet?.name,
      num_devices: userAttributes?.numberOfDevices,
      creation_date: userAttributes?.creationDate,
      age_in_days: userAttributes?.ageInDays,
    },
  };

  datadogRUM({ configuration, user });
};

export const configureNps = (session: Session, userAttributes: UserAttributes, deal?: Deal) => {
  if (env.name === 'prod') {
    const { numberOfDevices } = userAttributes;

    const fleetSize = () => {
      if (numberOfDevices <= 5) {
        return 'Micro';
      }

      if (numberOfDevices <= 15) {
        return 'Small';
      }

      if (numberOfDevices <= 50) {
        return 'Medium';
      }

      return 'Large';
    };

    // This should be in ISO8601 format. Formatting example (for the current time): 2023-10-26T14:16:23Z.
    const createdAt = moment(session.user.creationDate).format('YYYY-MM-DDTHH:mm:ssZ');
  }
};

export const configureClarity = ({ user, fleet }: Session) => {
  if (env.clarityId) {
    clarity.init(env.clarityId);

    if (clarity.hasStarted()) {
      clarity.setTag('fleet_id', fleet.id);
      clarity.setTag('fleet_name', fleet.name);
      clarity.setTag('user_id', user.id);
      clarity.setTag('user_email', user.email);
      clarity.identify(user.id, {
        email: user.email,
        username: user.name,
        fleet_id: fleet.id,
        fleet_name: fleet.name,
      });

      clarity.upgrade('Session started with data user');
    }
  }
};
